import { useCallback, useEffect, useRef } from "react";
import { apiLive } from "@/lib/api";

type FetchOptions = {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
};

/**
 * Base hook for making authenticated requests to the Cal.com v2 API proxy.
 * Handles token refresh automatically through the backend.
 */
export function useCalApi() {
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup pending requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchCalApi = useCallback(
    async <T>(endpoint: string, options: FetchOptions = {}): Promise<T | null> => {
      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      try {
        const { method = "GET", body } = options;

        // All requests go through our API proxy which handles auth
        const url = `/api/calendar/v2${endpoint}`;

        if (method === "GET") {
          return await apiLive<T>(url);
        }

        // For other methods, we need to use fetch directly
        const response = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
          },
          body: body ? JSON.stringify(body) : undefined,
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          console.error(`Cal API error: ${response.status} ${response.statusText}`);
          return null;
        }

        return await response.json();
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          // Request was cancelled, don't log as error
          return null;
        }
        console.error("Cal API error:", error);
        return null;
      }
    },
    []
  );

  return { fetchCalApi };
}
